import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { RegionService } from '../../../../core/services/region.service';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Region } from '../../../../core/models/region';
import { RegexConstants } from '../../../../core/constants/regex-constants';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { RequiredMarkDirective } from '../../../../shared/directives/required-mark.directive';

@Component({
  selector: 'em-region-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, InputTextModule, ButtonModule, TranslatePipe, RequiredMarkDirective],
  templateUrl: './region-form.component.html',
  styleUrl: './region-form.component.scss',
  providers: [],
})
export class RegionFormComponent {
  form!: FormGroup;
  formErrors: any;
  isEditMode: boolean = false;
  errorMessages: any = {};
  translations: any = {};
  edited: boolean = false;

  constructor(
    private fb: FormBuilder,
    private regionService: RegionService,
    private ref: DynamicDialogRef,
    private messageService: MessageService,
    private config: DynamicDialogConfig,
    private translate: TranslateService,
  ) { }

  ngOnInit() {
    this.loadTranslations();
    this.loadErrorMessages();
    this.form = this.fb.group({
      id: [null],
      name: [null, Validators.compose([
        Validators.required,
        Validators.pattern(RegexConstants.REGION_NAME)])],
      nameEs: [null,
        Validators.compose([
          Validators.required,
          Validators.pattern(RegexConstants.REGION_NAME_ES)])],
      nameAr: [null,
        Validators.compose([
          Validators.required,
          Validators.pattern(RegexConstants.REGION_NAME_AR)])],
      code: [null,
        Validators.compose([
          Validators.required,
          Validators.pattern(RegexConstants.COUNTRY_REGION_CODE)])],
    });

    if (this.config.data) {
      this.isEditMode = true;
      this.populateForm(this.config.data);
    }
    this.translate.onLangChange.subscribe(() => {
      this.loadErrorMessages();
    });
    this.form.valueChanges.subscribe(() => {
      this.edited = true;
    });
  }

  loadErrorMessages(): void {
    this.translate
      .get([
        'layout.configurations.regions.regionsValidators.name.required',
        'layout.configurations.regions.regionsValidators.nameEs.required',
        'layout.configurations.regions.regionsValidators.nameAr.required',
        'layout.configurations.regions.regionsValidators.code.required',
        'layout.configurations.regions.regionsValidators.name.pattern',
        'layout.configurations.regions.regionsValidators.nameEs.pattern',
        'layout.configurations.regions.regionsValidators.nameAr.pattern',
        'layout.configurations.regions.regionsValidators.code.pattern',
      ])
      .subscribe((translations) => {
        this.errorMessages = {
          required: {
            name: translations['layout.configurations.regions.regionsValidators.name.required'],
            nameEs: translations['layout.configurations.regions.regionsValidators.nameEs.required'],
            nameAr: translations['layout.configurations.regions.regionsValidators.nameAr.required'],
            code: translations['layout.configurations.regions.regionsValidators.code.required'],
          },
          pattern: {
            name: translations['layout.configurations.regions.regionsValidators.name.pattern'],
            nameEs: translations['layout.configurations.regions.regionsValidators.nameEs.pattern'],
            nameAr: translations['layout.configurations.regions.regionsValidators.nameAr.pattern'],
            code: translations['layout.configurations.regions.regionsValidators.code.pattern'],
          },
        };
      });
  }

  loadTranslations() {
    this.translate
      .get([
        'layout.configurations.regions.addRegion.errorMessage.alreadyExists',
        'layout.configurations.regions.editRegion.errorMessage.alreadyExists',
        'layout.configurations.regions.addRegion.errorMessage.title',
        'layout.configurations.regions.editRegion.errorMessage.title',
        'layout.configurations.regions.addRegion.successMessage.title',
        'layout.configurations.regions.editRegion.successMessage.title',
      ])
      .subscribe((translations) => {
        this.translations = translations;
      });
  }

  populateForm(region: Region) {
    this.form.patchValue({
      id: region.id,
      name: region.name,
      nameEs: region.nameEs,
      nameAr: region.nameAr,
      code: region.code,
    });
  }

  onSave() {
    if (this.form.invalid) {
      Object.values(this.form.controls).forEach((control) => {
        control.markAsTouched();
      });
      return;
    }

    if (this.isEditMode) {
      this.updateRegion();
      this.edited = false;
    } else {
      this.addRegion();
    }
  }

  addRegion() {
    this.regionService.addRegion(this.form.value).subscribe({
      next: (region: Region) => {
        this.ref.close(region);
      },
      error: (error) => {
        this.formErrors = error?.error?.fieldErrors;
        console.log('Error while saving the region: ', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translations['layout.configurations.regions.addRegion.errorMessage.title'],
          detail: this.translations['layout.configurations.regions.addRegion.errorMessage.alreadyExists'],
        });
      },
    });
  }

  updateRegion() {
    if (!this.edited) {
      this.ref.close({ war: 'warning' });
      return;
    }
    this.regionService.updateRegion(this.form.value).subscribe({
      next: (region: Region) => {
        this.ref.close({ succes: 'succes' });
      },
      error: (error) => {
        this.formErrors = error?.error?.fieldErrors;
        console.log('Error while updating the region: ', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translations['layout.configurations.regions.editRegion.errorMessage.title'],
          detail: this.translations['layout.configurations.regions.editRegion.errorMessage.alreadyExists'],
        });
      },
    });
  }

  isInvalid(key: string) {
    const control = this.form.get(key);

    return {
      invalid: control?.invalid && (control?.dirty || control?.touched),
      message:
        control?.errors &&
        (control.errors['required'] ? this.errorMessages.required[key]
          : control.errors['minlength']
            ? this.errorMessages.minLength[key]
            : this.errorMessages.pattern[key]),
    };
  }

  getErrorMessage(field: string) {
    return this.formErrors?.find((obj: { field: string }) => obj.field == field)?.errorMessage || '';
  }
}
